import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/format';
import { getErrorMessage } from '../../lib/errors';
import { platformLabel } from '../../lib/platforms';
import type {
  AppliedMark,
  CampaignProposal,
  ContentGoalsProposal,
  PlannedPostProposal,
  PlanningProposal,
  PostType,
  ProposalStatus,
} from '../../types/api';
import { monthLabel, monthOf } from '../calendar/calendarDates';
import { applyProposal } from './applyProposal';

/** Worded as on the generation form, so the two screens name the same thing alike. */
const POST_TYPE_LABELS: Readonly<Record<PostType, string>> = {
  single_image: 'Single image',
  carousel: 'Carousel',
  text_only: 'Text only',
};

/** The kind of record a proposal would become, for the card's eyebrow. */
const PROPOSAL_LABELS: Readonly<Record<PlanningProposal['type'], string>> = {
  planned_post: 'Planned post',
  campaign: 'Campaign',
  content_goals: 'Monthly goals',
};

/** `4 Sep 2026 – 20 Sep 2026`, or one end of it, or nothing at all. */
function dateRange(startsOn: string | null | undefined, endsOn: string | null | undefined): string {
  if (startsOn && endsOn) return `${formatDate(startsOn)} – ${formatDate(endsOn)}`;
  if (startsOn) return `From ${formatDate(startsOn)}`;
  if (endsOn) return `Until ${formatDate(endsOn)}`;
  return 'No dates';
}

function PlannedPostBody({ proposal }: { proposal: PlannedPostProposal }) {
  return (
    <>
      <p className="proposal-card__title">{proposal.title}</p>
      <p className="proposal-card__data">{formatDate(proposal.scheduled_on)}</p>
      {proposal.platform || proposal.post_type ? (
        <p className="proposal-card__chips">
          {proposal.platform ? (
            <span className="proposal-card__chip">{platformLabel(proposal.platform)}</span>
          ) : null}
          {proposal.post_type ? (
            <span className="proposal-card__chip">{POST_TYPE_LABELS[proposal.post_type]}</span>
          ) : null}
        </p>
      ) : null}
      {proposal.brief ? <p className="proposal-card__text">{proposal.brief}</p> : null}
    </>
  );
}

function CampaignBody({ proposal }: { proposal: CampaignProposal }) {
  return (
    <>
      <p className="proposal-card__title">
        <span
          className="proposal-card__swatch"
          style={{ background: proposal.color }}
          aria-hidden="true"
        />
        {proposal.name}
      </p>
      <p className="proposal-card__data">{dateRange(proposal.starts_on, proposal.ends_on)}</p>
      {proposal.description ? <p className="proposal-card__text">{proposal.description}</p> : null}
    </>
  );
}

function ContentGoalsBody({ proposal }: { proposal: ContentGoalsProposal }) {
  const cadence = Object.entries(proposal.cadence_targets ?? {}).filter(([, weekly]) => weekly > 0);
  const metrics = proposal.metric_targets ?? [];

  return (
    <>
      <p className="proposal-card__title">Goals for {monthLabel(monthOf(proposal.month))}</p>
      {proposal.focus ? <p className="proposal-card__text">{proposal.focus}</p> : null}

      {cadence.length > 0 ? (
        <p className="proposal-card__chips">
          {cadence.map(([platform, weekly]) => (
            <span className="proposal-card__chip" key={platform}>
              {platformLabel(platform)} <span className="proposal-card__figure">{weekly}</span> a
              week
            </span>
          ))}
        </p>
      ) : null}

      {metrics.length > 0 ? (
        <ul className="proposal-card__metrics">
          {metrics.map((metric) => (
            <li className="proposal-card__metric" key={metric.label}>
              <span>{metric.label}</span>
              <span className="proposal-card__figure">{metric.target}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* The PUT behind this merges: anything the proposal leaves out stays put. */}
      <p className="proposal-card__note">Updates this month’s goals.</p>
    </>
  );
}

function ProposalBody({ proposal }: { proposal: PlanningProposal }) {
  switch (proposal.type) {
    case 'planned_post':
      return <PlannedPostBody proposal={proposal} />;
    case 'campaign':
      return <CampaignBody proposal={proposal} />;
    case 'content_goals':
      return <ContentGoalsBody proposal={proposal} />;
  }
}

interface ProposalCardProps {
  proposal: PlanningProposal;
  businessId: string;
  /** The decision already recorded for this proposal, or null while undecided. */
  mark: AppliedMark | null;
  /** Another apply is running on this message; the card's own buttons stand down. */
  isLocked?: boolean;
  /** Files the decision against the message row once the write has landed. */
  onMark: (status: ProposalStatus, resourceId?: string) => Promise<unknown>;
}

/**
 * One thing the assistant's planner suggests, and the two things a user can do about it.
 *
 * A proposal is advice, not a record: nothing exists until Apply writes it
 * through the calendar's own endpoints. Dismissing writes nothing at all beyond
 * the mark that says the user has seen it, so a dismissed card is muted rather
 * than removed — the transcript is a log of what was suggested, including what
 * was turned down.
 */
export function ProposalCard({
  proposal,
  businessId,
  mark,
  isLocked = false,
  onMark,
}: ProposalCardProps) {
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function apply() {
    setIsBusy(true);
    setError(null);
    try {
      const resourceId = await applyProposal(businessId, proposal);
      try {
        await onMark('applied', resourceId);
      } catch (caught) {
        // The record exists; only the bookkeeping failed. Say which, so the user
        // does not apply it a second time to fix it.
        setError(
          getErrorMessage(
            caught,
            'Saved, but the assistant did not record it. Reload before applying again.',
          ),
        );
      }
    } catch (caught) {
      setError(getErrorMessage(caught, 'That proposal did not save.'));
    } finally {
      setIsBusy(false);
    }
  }

  async function dismiss() {
    setIsBusy(true);
    setError(null);
    try {
      await onMark('dismissed');
    } catch (caught) {
      setError(getErrorMessage(caught, 'That proposal could not be dismissed.'));
    } finally {
      setIsBusy(false);
    }
  }

  const state =
    mark === null
      ? ''
      : mark.status === 'applied'
        ? ' proposal-card--applied'
        : ' proposal-card--dismissed';

  return (
    <article className={`proposal-card${state}`}>
      <p className="proposal-card__kind">{PROPOSAL_LABELS[proposal.type]}</p>

      <ProposalBody proposal={proposal} />

      {mark === null ? (
        <div className="proposal-card__actions">
          <button
            className="button button--small"
            type="button"
            disabled={isBusy || isLocked}
            onClick={() => void apply()}
          >
            {isBusy ? 'Applying' : 'Apply'}
          </button>
          <button
            className="button button--ghost button--small"
            type="button"
            disabled={isBusy || isLocked}
            onClick={() => void dismiss()}
          >
            Dismiss
          </button>
        </div>
      ) : mark.status === 'applied' ? (
        <p className="proposal-card__mark" role="status">
          <span className="proposal-card__mark-label">Applied</span>
          {proposal.type === 'planned_post' ? (
            <Link
              className="proposal-card__link"
              to={`/calendar?month=${monthOf(proposal.scheduled_on)}`}
            >
              Open on the calendar
            </Link>
          ) : null}
        </p>
      ) : (
        <p className="proposal-card__mark">
          <span className="proposal-card__mark-label">Dismissed</span>
        </p>
      )}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}
    </article>
  );
}
